import React from 'react'
import { Card, CardContent } from './Card'
import { CopyButton } from './CopyButton'
import { Loading } from './Loading'
import { cn } from './utils'

export interface DataDisplayProps extends React.HTMLAttributes<HTMLDivElement> {
  data: string | null
  loading?: boolean
  label?: string
  placeholder?: string
  showCopy?: boolean
  copyButtonProps?: React.ComponentProps<typeof CopyButton>
}

const DataDisplay = React.forwardRef<HTMLDivElement, DataDisplayProps>(
  ({ 
    className, 
    data, 
    loading = false, 
    label, 
    placeholder = 'Generated data will appear here...',
    showCopy = true,
    copyButtonProps,
    ...props 
  }, ref) => {
    return (
      <div ref={ref} className={cn('space-y-2', className)} {...props}>
        {label && (
          <label className="text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        <Card variant="default" padding="sm">
          <CardContent className="relative">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loading size="md" variant="spinner" />
              </div>
            ) : (
              <>
                <div className="min-h-[2.5rem] flex items-center">
                  {data ? (
                    <code className="text-sm font-mono text-foreground break-all">
                      {data}
                    </code>
                  ) : (
                    <span className="text-sm text-muted-foreground italic">
                      {placeholder}
                    </span>
                  )}
                </div>
                {data && showCopy && (
                  <div className="absolute top-2 right-2">
                    <CopyButton 
                      value={data} 
                      size="icon" 
                      variant="ghost"
                      {...copyButtonProps}
                    />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }
)

DataDisplay.displayName = 'DataDisplay'

export { DataDisplay }
